import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  LocalHospital,
  PointOfSale,
  Print,
  QrCode2,
  ShoppingCart,
  WarningAmber,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "../../../../context";
import BarcodeScanner from "../../../../Components/BarcodeScanner/BarcodeScanner";
import MedicineLabelPreview from "../../../../Components/BarcodeScanner/MedicineLabelPreview";
import { printMedicineLabel } from "../../../../Components/BarcodeScanner/printMedicineLabel";
import { playScanBeep } from "../../../../Components/BarcodeScanner/playScanBeep";
import useBarcodeLookup from "../../../../api/useBarcodeLookup";
import { showStockQtyAlert, createScanQueue } from "../utils/scanCart";

const statusColor = (status) => {
  if (status === "in_stock") return "success";
  if (status === "low_stock") return "warning";
  if (status === "expired") return "error";
  return "default";
};

const Scanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token;
  const { lookup } = useBarcodeLookup(token);

  const [result, setResult] = useState(null);
  const [cart, setCart] = useState([]);
  const cartRef = useRef([]);
  const scanQueueRef = useRef(createScanQueue());

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  const handleScan = useCallback(
    (barcode) => {
      scanQueueRef.current(async () => {
        const data = await lookup(barcode, { complete: true });
        setResult(data);

        if (!data.found) {
          playScanBeep(false);
          toast.error(
            data.status === "error"
              ? t("pharmacy.barcodeLookupFailed")
              : t("pharmacy.barcodeNotFound", { barcode }),
          );
          return;
        }

        const medicineName = data.medicine?.name || barcode;
        const available = Number(
          data.total_quantity ?? data.best_stock?.quantity ?? 0,
        );

        if (available <= 0) {
          playScanBeep(false);
          toast.error(t("pharmacy.barcodeOutOfStock", { name: medicineName }));
          return;
        }

        const currentQty = cartRef.current.reduce((sum, item) => {
          const sameMedicine =
            String(item.medicine?.id) === String(data.medicine?.id) ||
            String(item.barcode || "").toLowerCase() ===
              String(data.barcode || "").toLowerCase();
          return sameMedicine ? sum + (Number(item.quantity) || 0) : sum;
        }, 0);

        if (currentQty + 1 > available) {
          playScanBeep(false);
          showStockQtyAlert(
            { reason: "exceeds_stock", available, currentQty },
            medicineName,
            t,
            toast,
          );
          return;
        }

        const existingIndex = cartRef.current.findIndex(
          (item) =>
            String(item.medicine?.id) === String(data.medicine?.id) ||
            String(item.barcode || "").toLowerCase() ===
              String(data.barcode || "").toLowerCase(),
        );

        let nextCart;
        if (existingIndex >= 0) {
          nextCart = cartRef.current.map((item, index) =>
            index === existingIndex
              ? { ...item, quantity: (Number(item.quantity) || 0) + 1 }
              : item,
          );
        } else {
          nextCart = [
            ...cartRef.current,
            {
              ...data,
              quantity: 1,
              scannedAt: new Date().toISOString(),
            },
          ];
        }

        cartRef.current = nextCart;
        setCart(nextCart);

        playScanBeep(true);
        toast.success(t("pharmacy.barcodeScanned", { name: medicineName }), {
          toastId: `scan-${data.barcode || barcode}`,
        });
      });
    },
    [lookup, t],
  );

  const updateCartQty = (index, quantity) => {
    setCart((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, quantity: Math.max(1, Number(quantity) || 1) } : item,
      ),
    );
  };

  const removeCartItem = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  const sellableItems = useMemo(
    () => cart.filter((item) => item.best_stock && item.found),
    [cart],
  );
  const purchasableItems = useMemo(
    () => cart.filter((item) => item.medicine?.id && item.found),
    [cart],
  );

  const goToPos = (items = sellableItems) => {
    navigate("/pharmacy/pos/add", {
      state: items.length
        ? {
            scannedItems: items.map((item) => ({
              ...item.best_stock,
              scan_quantity: item.quantity || 1,
            })),
          }
        : {},
    });
  };

  const goToSale = () => {
    if (!sellableItems.length) {
      toast.info(t("pharmacy.openingSaleEmpty"));
    }
    goToPos();
  };

  const addCurrentToPos = () => {
    if (!result?.best_stock || Number(result.best_stock.quantity) <= 0) {
      toast.error(t("pharmacy.noStockToSell"));
      goToPos([]);
      return;
    }
    goToPos([
      {
        ...result,
        best_stock: result.best_stock,
        quantity: 1,
      },
    ]);
  };

  const goToPurchase = () => {
    if (!purchasableItems.length) {
      toast.error(t("pharmacy.noMedicineToPurchase"));
      return;
    }
    navigate("/pharmacy/purchase-drug/add", {
      state: {
        scannedMedicines: purchasableItems.map((item) => ({
          ...item.medicine,
          scan_quantity: item.quantity,
        })),
      },
    });
  };

  const goToPrescription = () => {
    if (!sellableItems.length) {
      toast.error(t("pharmacy.noStockToPrescribe"));
      return;
    }
    navigate("/pharmacy/prescription/add", {
      state: {
        scannedItems: sellableItems.map((item) => ({
          ...item.best_stock,
          scan_quantity: item.quantity,
        })),
      },
    });
  };

  const registerMissing = () => {
    navigate("/pharmacy/new-medicine", {
      state: { barcode: result?.barcode || "" },
    });
  };

  const handlePrintLabel = async (mode = "both") => {
    const barcode = result?.medicine?.barcode || result?.barcode;
    if (!barcode) {
      toast.error(t("pharmacy.noBarcodeToPrint"));
      return;
    }
    const ok = await printMedicineLabel({
      barcode,
      name: result?.medicine?.name,
      category:
        result?.medicine?.category_display || result?.best_stock?.category_display,
      price: result?.best_stock?.sell_price,
      expiry: result?.best_stock?.expiry_date,
      mode,
    });
    if (!ok) toast.error(t("pharmacy.printBlocked"));
  };

  const handlePrintCartLabels = async () => {
    const items = cart.filter((item) => item.barcode || item.medicine?.barcode);
    if (!items.length) {
      toast.error(t("pharmacy.noBarcodeToPrint"));
      return;
    }
    for (const item of items) {
      await printMedicineLabel({
        barcode: item.medicine?.barcode || item.barcode,
        name: item.medicine?.name,
        category: item.medicine?.category_display,
        price: item.best_stock?.sell_price,
        mode: "both",
      });
    }
    toast.success(t("pharmacy.labelsPrinted", { count: items.length }));
  };

  return (
    <Grid container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper
        sx={{
          width: "100%",
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary">
              {t("pharmacy.scannerTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("pharmacy.scannerSubtitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {t("pharmacy.scannerSupportsQr")}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              startIcon={<PointOfSale />}
              onClick={() => goToPos()}
            >
              {t("pharmacy.sale")}
            </Button>
            <Chip
              color="success"
              variant="outlined"
              label={t("pharmacy.hardwareReady")}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(15,23,42,0.04))",
            border: "1px dashed rgba(25,118,210,0.35)",
            position: "relative",
          }}
        >
          <BarcodeScanner onScan={handleScan} autoFocus listenHardware />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                {t("pharmacy.lastScan")}
              </Typography>
              {!result ? (
                <Alert severity="info">{t("pharmacy.scanToStart")}</Alert>
              ) : !result.found ? (
                <Stack spacing={2}>
                  <Alert severity="warning" icon={<WarningAmber />}>
                    {t("pharmacy.barcodeNotFound", { barcode: result.barcode })}
                  </Alert>
                  <Button variant="contained" onClick={registerMissing}>
                    {t("pharmacy.registerThisBarcode")}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {result.medicine?.name}
                    </Typography>
                    <Typography color="text.secondary">
                      {result.medicine?.category_display ||
                        result.best_stock?.category_display}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      color={statusColor(result.status)}
                      label={t(`pharmacy.stockStatus.${result.status}`, {
                        defaultValue: result.status,
                      })}
                    />
                    <Chip
                      label={`${t("pharmacy.availableStock")}: ${
                        result.total_quantity ?? 0
                      }`}
                    />
                    {result.best_stock?.sell_price != null && (
                      <Chip
                        label={`${t("pharmacy.price")}: ${result.best_stock.sell_price}`}
                      />
                    )}
                  </Stack>
                  <MedicineLabelPreview
                    barcode={result.medicine?.barcode || result.barcode}
                    name={result.medicine?.name}
                    category={
                      result.medicine?.category_display ||
                      result.best_stock?.category_display
                    }
                    price={result.best_stock?.sell_price}
                    expiry={result.best_stock?.expiry_date}
                  />
                  {result.medicine?.description && (
                    <Typography variant="body2" color="text.secondary">
                      {result.medicine.description}
                    </Typography>
                  )}
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("pharmacy.expiryDate")}</TableCell>
                        <TableCell align="center">{t("pharmacy.stock")}</TableCell>
                        <TableCell align="center">{t("pharmacy.price")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(result.stock || []).length ? (
                        result.stock.map((lot) => (
                          <TableRow key={lot.id}>
                            <TableCell>
                              {lot.expiry_date}
                              {lot.is_expired ? ` (${t("pharmacy.expired")})` : ""}
                            </TableCell>
                            <TableCell align="center">{lot.quantity}</TableCell>
                            <TableCell align="center">{lot.sell_price}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">
                            {t("pharmacy.noStockLots")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t("pharmacy.printSection")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {t("pharmacy.printSectionHint")}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      startIcon={<Print />}
                      variant="outlined"
                      onClick={() => handlePrintLabel("barcode")}
                    >
                      {t("pharmacy.printBarcode")}
                    </Button>
                    <Button
                      startIcon={<QrCode2 />}
                      variant="outlined"
                      onClick={() => handlePrintLabel("qr")}
                    >
                      {t("pharmacy.printQrCode")}
                    </Button>
                    <Button
                      startIcon={<Print />}
                      variant="contained"
                      onClick={() => handlePrintLabel("both")}
                    >
                      {t("pharmacy.printBothCodes")}
                    </Button>
                  </Stack>
                  <Button
                    startIcon={<PointOfSale />}
                    variant="contained"
                    onClick={addCurrentToPos}
                  >
                    {t("pharmacy.addToSale")}
                  </Button>
                </Stack>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" fontWeight={700}>
                  {t("pharmacy.scannedItems")}
                </Typography>
                <Button
                  color="inherit"
                  disabled={!cart.length}
                  onClick={() => setCart([])}
                >
                  {t("pharmacy.clearScans")}
                </Button>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#eef2f5" }}>
                    <TableRow>
                      <TableCell>{t("pharmacy.medicine")}</TableCell>
                      <TableCell>{t("pharmacy.barcode")}</TableCell>
                      <TableCell align="center">{t("pharmacy.stock")}</TableCell>
                      <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                      <TableCell align="center">{t("pharmacy.action")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.length ? (
                      cart.map((item, index) => (
                        <TableRow key={`${item.barcode}-${index}`} hover>
                          <TableCell>
                            <Typography fontWeight={600}>
                              {item.medicine?.name || t("pharmacy.unknown")}
                            </Typography>
                            <Chip
                              size="small"
                              color={statusColor(item.status)}
                              label={t(`pharmacy.stockStatus.${item.status}`, {
                                defaultValue: item.status,
                              })}
                              sx={{ mt: 0.5 }}
                            />
                          </TableCell>
                          <TableCell>{item.barcode}</TableCell>
                          <TableCell align="center">
                            {item.total_quantity ?? 0}
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(event) =>
                                updateCartQty(index, event.target.value)
                              }
                              inputProps={{ min: 1, style: { width: 70 } }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t("pharmacy.removeItem")}>
                              <IconButton
                                color="error"
                                onClick={() => removeCartItem(index)}
                              >
                                <DeleteOutline />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {t("pharmacy.emptyScanCart")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3 }} />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  disabled={!cart.length}
                  onClick={handlePrintCartLabels}
                >
                  {t("pharmacy.printCartLabels")}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PointOfSale />}
                  onClick={goToSale}
                >
                  {t("pharmacy.sendToSale")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ShoppingCart />}
                  disabled={!purchasableItems.length}
                  onClick={goToPurchase}
                >
                  {t("pharmacy.completePurchase")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LocalHospital />}
                  disabled={!sellableItems.length}
                  onClick={goToPrescription}
                >
                  {t("pharmacy.completePrescription")}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default Scanner;
